import type { WebSocketPayload } from '@/provider/webSocket/type';
import type {
  DisplayedNotification,
  FrameData,
  Project,
  RawNotification,
  Task,
} from '@/type';

import type { NotificationTransformer } from './type';

const createAlarmTypeTransformer =
  (
    alarmType: WebSocketPayload<'MiriAlarmLog'>['alarmType'] | 'Any',
    transform: (notification: DisplayedNotification) => DisplayedNotification,
  ): NotificationTransformer =>
  (notification: DisplayedNotification) =>
    alarmType === 'Any' || notification.raw.alarmType === alarmType
      ? transform(notification)
      : notification;

const createBaseNotification = (
  raw: RawNotification,
): DisplayedNotification => {
  const { createdAt, id, read, user } = raw;

  return {
    createdAt,
    from: user,
    id,
    raw,
    read,
    content: '',
    description: '',
    emphasisText: '',
  };
};

const transformAny: NotificationTransformer = createAlarmTypeTransformer(
  'Any',
  notification => notification,
);
const transformMiriTaskAIAnnotationDone: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskAIAnnotationDone', notification => ({
    ...notification,
    content: 'AI Annotation has been completed',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
  }));

const transformMiriTaskAIAnnotationFail: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskAIAnnotationFail', notification => ({
    ...notification,
    content: 'AI Annotation has failed',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
  }));

const transformDownloadFolderZipDone: NotificationTransformer =
  createAlarmTypeTransformer('DownloadFolderZipDone', notification => ({
    ...notification,
    content: 'The folder has been compressed. Starting the download',
    emphasisText: notification.raw.reportPayload.filePath,
  }));

const transformDownloadFolderZipFail: NotificationTransformer =
  createAlarmTypeTransformer('DownloadFolderZipFail', notification => ({
    ...notification,
    content: notification.raw.reportMsg,
  }));

const transformMiriTaskGenerateDone: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskGenerateDone', notification => ({
    ...notification,
    content: 'A new task has been created',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
    description: notification.raw.reportPayload.taskName,
  }));

const transformMiriTaskGenerateFail: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskGenerateFail', notification => ({
    ...notification,
    content: 'Task creation has failed',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
    description: notification.raw.reportPayload.taskName,
  }));

const transformMiriTaskExportLabelDone: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskExportLabelDone', notification => ({
    ...notification,
    content: 'Task has been completed',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
  }));

const transformMiriTaskExportLabelFail: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskExportLabelFail', notification => ({
    ...notification,
    content: 'Failed to extract label data from task',
    emphasisText: `Task ${notification.raw.reportPayload.taskID}`,
  }));

const transformMiriDeleteFolderDone: NotificationTransformer =
  createAlarmTypeTransformer('MiriDeleteFolderDone', notification => ({
    ...notification,
    content: 'Folder deletion has been completed',
    emphasisText: notification.raw.reportPayload.folderPATH,
  }));

const transformMiriDeleteFolderFail: NotificationTransformer =
  createAlarmTypeTransformer('MiriDeleteFolderFail', notification => ({
    ...notification,
    content: 'Folder deletion has failed',
    emphasisText: notification.raw.reportPayload.folderPATH,
  }));

const transformMiriTaskBulkUpdateFrameState: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskBulkUpdateFrameState', notification => {
    const frameMap =
      (notification.raw.reportPayload.frame as {
        [frameSeq: string]: Pick<
          FrameData,
          'frameSeq' | 'reviewerID' | 'taskID' | 'workState' | 'workerID'
        > & {
          reviewerName: string;
          taskName: string;
          workerName: string;
        };
      }) ?? {};
    const frameSeqList = Object.keys(frameMap);
    const firstFrame = frameMap[frameSeqList[0]];
    const lastFrame = frameMap[frameSeqList[frameSeqList.length - 1]];

    if (firstFrame && lastFrame) {
      const workState = firstFrame.workState;

      return {
        ...notification,
        content: `Frames ${firstFrame.frameSeq} ~ ${lastFrame.frameSeq} have been changed to "${workState}"`,
      };
    }

    return {
      ...notification,
    };
  });
const transformMiriTaskUpdateFrameState: NotificationTransformer =
  createAlarmTypeTransformer('MiriTaskUpdateFrameState', notification => {
    const { taskID, frameSeq } = notification.raw.reportPayload;

    return {
      ...notification,
      content: `Frame has been submitted`,
      emphasisText: `Task ${taskID} / Frame ${frameSeq}`,
    };
  });
const transformModelTrainingDoing: NotificationTransformer =
  createAlarmTypeTransformer('ModelTrainingDoing', notification => {
    const { miriModeling } = notification.raw.reportPayload;

    if (!miriModeling) return notification;

    const { name, version } = miriModeling;

    return {
      ...notification,
      content: `Training start`,
      emphasisText: `Model ${name} / Version ${version}`,
    };
  });
const transformModelTrainingDone: NotificationTransformer =
  createAlarmTypeTransformer('ModelTrainingDone', notification => {
    const { miriModeling } = notification.raw.reportPayload;

    if (!miriModeling) return notification;

    const { name, version } = miriModeling;

    return {
      ...notification,
      content: `Training complete`,
      emphasisText: `Model ${name} / Version ${version}`,
    };
  });

const transformTrackIDUpdateRequest: NotificationTransformer =
  createAlarmTypeTransformer('TrackIDUpdateRequest', notification => {
    const { projectID, taskID, trackID, newTrackID, startFrameSeq } =
      notification.raw.reportPayload as {
        projectID: Project['id'];
        taskID: Task['id'];
        trackID: string;
        newTrackID: string;
        startFrameSeq: FrameData['frameSeq'];
      };
    const userName =
      notification.raw.user.name || notification.raw.user.email.split('@')[0];

    if (!taskID || !trackID || !newTrackID || !startFrameSeq)
      return notification;

    return {
      ...notification,
      content: `${userName} requested to update track ID`,
      emphasisText: `Frame ${startFrameSeq}`,
      description: `Track ID ${trackID} → ${newTrackID}`,
      link: {
        to: `/labeling/${projectID}/${taskID}/${startFrameSeq}?trackID=${trackID}&newTrackID=${newTrackID}&requestUserID=${notification.raw.user.id}`,
      },
    };
  });

const runPipeline = (
  initialValue: DisplayedNotification,
  transformerList: NotificationTransformer[],
): DisplayedNotification => {
  return transformerList.reduce(
    (acc, transformer) => transformer(acc),
    initialValue,
  );
};

export const makeNotification = (
  raw: RawNotification,
): DisplayedNotification => {
  const baseNotification = createBaseNotification(raw);

  const transformerList: NotificationTransformer[] = [
    transformAny,
    transformMiriTaskAIAnnotationDone,
    transformMiriTaskAIAnnotationFail,
    transformDownloadFolderZipDone,
    transformDownloadFolderZipFail,
    transformMiriTaskGenerateDone,
    transformMiriTaskGenerateFail,
    transformMiriTaskExportLabelDone,
    transformMiriTaskExportLabelFail,
    transformMiriDeleteFolderDone,
    transformMiriDeleteFolderFail,
    transformMiriTaskBulkUpdateFrameState,
    transformMiriTaskUpdateFrameState,
    transformModelTrainingDoing,
    transformModelTrainingDone,
    transformTrackIDUpdateRequest,
  ];

  return runPipeline(baseNotification, transformerList);
};
